'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Menu, X } from 'lucide-react'
import { HelpSidebar } from './help-sidebar'
import { HelpSearch } from './help-search'
import { HelpAskAi } from './help-ask-ai'
import { useFocusTrap } from '@/lib/use-focus-trap'
import type { HelpNavCategory, HelpSearchDoc } from '@/lib/help-nav'

// Client island that owns the docs left rail across both the index and
// every article: a persistent, sticky, scrollable sidebar on desktop
// and a slide-in drawer (hamburger) on mobile. The article body itself
// is passed in as `children` and stays server-rendered.
export function HelpShell({
  nav,
  index,
  children,
}: {
  nav: HelpNavCategory[]
  index: HelpSearchDoc[]
  children: React.ReactNode
}) {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [askOpen, setAskOpen] = useState(false)
  const [askQuery, setAskQuery] = useState<string | undefined>(undefined)
  const pathname = usePathname()
  const drawerRef = useRef<HTMLDivElement>(null)
  const menuTriggerRef = useRef<HTMLButtonElement>(null)

  // Handoff from Cmd-K search → Ask AI: carry the typed query across
  // and open the shared Ask-AI modal.
  const handleAskAi = (query: string) => {
    setAskQuery(query || undefined)
    setAskOpen(true)
  }

  // Close the mobile drawer on navigation.
  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!drawerOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [drawerOpen])

  // Had role="dialog"/aria-modal but no Escape handling, no focus trap,
  // and no initial-focus-move at all — the least accessible of the four
  // dialogs a 29 Aug 2026 re-audit found this gap in. Mirrors header.tsx's
  // mobile-nav treatment.
  useFocusTrap(drawerRef, drawerOpen)
  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const t = window.setTimeout(() => {
      drawerRef.current
        ?.querySelector<HTMLElement>('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')
        ?.focus()
    }, 0)
    return () => {
      document.removeEventListener('keydown', onKey)
      window.clearTimeout(t)
      menuTriggerRef.current?.focus()
    }
  }, [drawerOpen])

  return (
    <div className="container-wide">
      {/* Single docs toolbar — search + Ask AI live here ONCE, visible at
          every breakpoint. The hamburger (mobile only) opens the nav
          drawer. This is the sole place these controls render, so they
          can never read as duplicated against the sidebar. */}
      <div className="mb-6 flex items-center gap-3 border-b py-4 lg:mb-10">
        <button
          ref={menuTriggerRef}
          type="button"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open help navigation"
          aria-expanded={drawerOpen}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-card text-foreground transition hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 lg:hidden"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1 lg:max-w-2xl">
          <HelpSearch index={index} variant="compact" onAskAi={handleAskAi} />
        </div>
        <HelpAskAi variant="button" />
      </div>

      <div className="lg:grid lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-12 xl:grid-cols-[17rem_minmax(0,1fr)] xl:gap-16">
        {/* Desktop sidebar — navigation only */}
        <aside className="hidden lg:block">
          <div className="sticky top-24 max-h-[calc(100dvh-7rem)] overflow-y-auto pb-10 pr-2">
            <HelpSidebar nav={nav} />
          </div>
        </aside>

        {/* Page content (server-rendered article/index) */}
        <div className="min-w-0">{children}</div>
      </div>

      {/* Mobile slide-in drawer — navigation only */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[90] lg:hidden">
          <div
            className="absolute inset-0 bg-foreground/40 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Help navigation"
            className="absolute inset-y-0 left-0 flex w-[85%] max-w-xs flex-col border-r bg-background shadow-2xl"
          >
            <div className="flex items-center justify-between border-b px-4 py-3">
              <span className="font-display text-sm font-medium">Browse help</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close help navigation"
                className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <HelpSidebar nav={nav} />
            </div>
          </div>
        </div>
      )}

      {/* Shared, externally-controlled Ask-AI modal for the Cmd-K → Ask
          AI handoff. Its trigger is hidden; the single visible Ask AI
          button above renders its own modal instance. */}
      <HelpAskAi hideTrigger open={askOpen} onOpenChange={setAskOpen} initialQuery={askQuery} />
    </div>
  )
}
